import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { SVGIconComponent } from '../../../icons/types';
import { Tooltip } from '../../../tooltip/tooltip';
import { ActionsButton } from '../../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../../actions-button/actions-button.types';
import { UserStatusBadge } from '../../../user-status-badge/user-status-badge.component';
import { RegistrationPolicyBadge } from '../../../registration-policy-badge/registration-policy-badge.component';
import { NeedsReviewBadge } from '../../../needs-review-badge/needs-review-badge.component';
import { useCarrierDetailsPageHeaderStyles } from './carrier-details-page-header.styles';

export interface CarrierDetailsPageHeaderProps {
  name: string;
  /** A short form of the name, which the kiosk identifies couriers by. */
  shortName?: string;
  image?: string;
  /** `active` or `deleted` — the only two a group holds. */
  status?: string;
  /** Informative only; it does not change the status. */
  review?: { label: string; tooltip: string };
  /** The container it is filed under, and the project that container is attached to. */
  container?: {
    name: string;
    projectName?: string;
    /** Where the container name links to — the carriers list it was reached from. */
    url: string;
  };
  /** Whether couriers may sign themselves up, at a kiosk and by QR. */
  kioskRegistrationPolicy?: string;
  qrRegistrationPolicy?: string;
  /** Empty means granted nothing in this scope, which is an answer rather than a gap. */
  capabilities: { Icon: SVGIconComponent; label: string; transactionTypeLabels: string[] }[];
  actions: LoadedActionsButtonItem[];
}

/** Built like the courier header, but a carrier has only its container above it, not a trail. */
export function CarrierDetailsPageHeader({
  name,
  shortName,
  image,
  status,
  review,
  container,
  kioskRegistrationPolicy,
  qrRegistrationPolicy,
  capabilities,
  actions
}: CarrierDetailsPageHeaderProps) {
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useCarrierDetailsPageHeaderStyles();

  return (
    <Grid container sx={{ flexDirection: isSM ? 'column' : 'row' }} className={classes.root}>
      <Grid
        container
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container sx={{ flexDirection: 'column' }}>
          <Grid className={classes.carrierName}>
            <h2>{name}</h2>
            {/* `condensed` drops the badge's fixed 140px so it hugs its label beside the name. */}
            {status ? <UserStatusBadge status={status} condensed showTooltip={false} /> : null}
            {review ? <NeedsReviewBadge label={review.label} tooltipLabel={review.tooltip} /> : null}
          </Grid>
          {container ? (
            <Grid>
              <p className={classes.subtitle}>
                {container.projectName ? (
                  <>
                    <span>{container.projectName}</span>
                    <span className={classes.subtitleSeparator}>&gt;</span>
                  </>
                ) : null}
                <Link className={classes.subtitleLink} to={container.url}>
                  {container.name}
                </Link>
              </p>
            </Grid>
          ) : null}
          {image ? (
            <Grid>
              <img className={classes.carrierImage} src={image} alt={name} />
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container sx={{ flexDirection: 'column' }}>
          <Grid className={classes.capabilities}>
            <span>Capabilities</span>
          </Grid>
          <Grid container direction="row" spacing={1}>
            {capabilities.length === 0 ? (
              <Grid>
                <span className={classes.info}>No capabilities</span>
              </Grid>
            ) : (
              capabilities.map((type) => (
                <Grid key={type.label}>
                  {/* One icon covers deliver and collect, so hovering names both. The span is
                      needed because the icons hold no ref. */}
                  <Tooltip title={type.transactionTypeLabels.join(' · ')} placement="bottom" width="auto">
                    <span>
                      <type.Icon className={classes.capabilitiesIcon} />
                    </span>
                  </Tooltip>
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
        <Grid container>
          <Grid>
            {shortName ? <p className={classes.info}>Short name: {shortName}</p> : null}
            <p className={classes.info}>
              Kiosk registration: <RegistrationPolicyBadge policy={kioskRegistrationPolicy} onDark />
            </p>
            <p className={classes.info}>
              QR registration: <RegistrationPolicyBadge policy={qrRegistrationPolicy} onDark />
            </p>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          md: 4
        }}
        sx={{
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <Grid container direction="row" spacing={2}>
          <div className={classes.actionButtonBox}>
            <ActionsButton itemArray={actions} />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
}
